import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Globe, Users, Lock } from "lucide-react";

export type PrivacyLevel = "public" | "friends_only" | "private";

interface PrivacySelectProps {
  value: PrivacyLevel;
  onChange: (value: PrivacyLevel) => void;
  label?: string;
  description?: string;
  showDescription?: boolean;
}

const privacyOptions = [
  {
    value: "public" as PrivacyLevel,
    label: "Public",
    icon: Globe,
    description: "Anyone can see this",
  },
  {
    value: "friends_only" as PrivacyLevel,
    label: "Friends Only",
    icon: Users,
    description: "Only your followers can see this",
  },
  {
    value: "private" as PrivacyLevel,
    label: "Private",
    icon: Lock,
    description: "Only you can see this",
  },
];

export function PrivacySelect({
  value,
  onChange,
  label,
  description,
  showDescription = true,
}: PrivacySelectProps) {
  const selectedOption = privacyOptions.find((opt) => opt.value === value);

  return (
    <div className="space-y-2">
      {label && <label className="text-sm font-medium text-gray-900">{label}</label>}
      {description && <p className="text-sm text-gray-600">{description}</p>}

      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="w-full">
          <SelectValue>
            {selectedOption && (
              <div className="flex items-center gap-2">
                <selectedOption.icon className="w-4 h-4" />
                <span>{selectedOption.label}</span>
              </div>
            )}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {privacyOptions.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              <div className="flex items-start gap-3 py-1">
                <option.icon className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <div className="flex flex-col">
                  <span className="font-medium">{option.label}</span>
                  {showDescription && (
                    <span className="text-xs text-gray-500">
                      {option.description}
                    </span>
                  )}
                </div>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export function PrivacyBadge({ privacy }: { privacy: PrivacyLevel }) {
  const option = privacyOptions.find((opt) => opt.value === privacy);

  if (!option) return null;

  const Icon = option.icon;

  const colorClasses = {
    public: "bg-blue-100 text-blue-700 border-blue-200",
    friends_only: "bg-green-100 text-green-700 border-green-200",
    private: "bg-gray-100 text-gray-700 border-gray-200",
  };

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-0 rounded-full text-xs font-medium border ${
        colorClasses[privacy]
      }`}
    >
      <Icon className="w-3 h-3" />
      {option.label}
    </span>
  );
}
